import { useEffect, useMemo, useState } from 'react';
import Modal from '@/components/ui/Modal';
import * as adminService from '@/services/adminService';
import { resolveAssetUrl } from '@/utils/assets';

const emptyForm = {
  cityId: '',
  name: '',
  category: 'gastronomia',
  description: '',
  logoUrl: '',
  imageUrl: '',
  address: '',
  lat: '',
  lng: '',
  phone: '',
  email: '',
  isActive: true,
};

const fieldClass = 'w-full rounded-lg border border-fg/10 bg-black/25 px-3 py-2 text-sm text-fg outline-none focus:border-accent';
const labelClass = 'text-xs font-bold uppercase tracking-[0.16em] text-fg/40';

export default function AdminPartnersPage() {
  const [partners, setPartners] = useState([]);
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const cityById = useMemo(() => new Map(cities.map((city) => [city.id, city])), [cities]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [partnersRes, citiesRes] = await Promise.all([
        adminService.partners.list({ limit: 100 }),
        adminService.cities.list({ limit: 100 }),
      ]);
      const nextCities = citiesRes.data.data || [];
      setPartners(partnersRes.data.data || []);
      setCities(nextCities);
      setForm((current) => ({ ...current, cityId: current.cityId || nextCities[0]?.id || '' }));
      setError('');
    } catch {
      setError('Não foi possível carregar os parceiros.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setForm({ ...emptyForm, cityId: cities[0]?.id || '' });
    setLogoFile(null);
    setLogoPreview('');
    setError('');
    setNotice('');
  };

  const editPartner = (partner) => {
    setEditingId(partner.id);
    setForm({
      cityId: partner.cityId || '',
      name: partner.name || '',
      category: partner.category || 'gastronomia',
      description: partner.description || '',
      logoUrl: partner.logoUrl || '',
      imageUrl: partner.imageUrl || '',
      address: partner.address || '',
      lat: partner.lat || '',
      lng: partner.lng || '',
      phone: partner.phone || '',
      email: partner.email || '',
      isActive: Boolean(partner.isActive),
    });
    setLogoFile(null);
    setLogoPreview(resolveAssetUrl(partner.logoUrl || partner.imageUrl));
    setError('');
    setNotice('');
    setModalOpen(true);
  };

  const savePartner = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setNotice('');

    const payload = {
      ...form,
      lat: form.lat === '' ? null : Number(form.lat),
      lng: form.lng === '' ? null : Number(form.lng),
    };

    try {
      let savedPartner;
      if (editingId) {
        const res = await adminService.partners.update(editingId, payload);
        savedPartner = res.data.data;
        setNotice('Parceiro atualizado com sucesso.');
      } else {
        const res = await adminService.partners.create(payload);
        savedPartner = res.data.data;
        setNotice('Parceiro criado com sucesso.');
      }

      if (logoFile && savedPartner?.id) {
        await adminService.uploadPartnerLogo(savedPartner.id, logoFile);
      }

      await loadData();
      resetForm();
      setModalOpen(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível guardar o parceiro.');
    } finally {
      setSaving(false);
    }
  };

  const selectLogo = (file) => {
    setLogoFile(file || null);
    setLogoPreview(file ? URL.createObjectURL(file) : resolveAssetUrl(form.logoUrl || form.imageUrl));
  };

  const toggleActive = async (partner) => {
    try {
      await adminService.partners.update(partner.id, { isActive: !partner.isActive });
      await loadData();
    } catch {
      setError('Não foi possível alterar o estado do parceiro.');
    }
  };

  const removePartner = async (partner) => {
    if (!window.confirm(`Remover o parceiro "${partner.name}"?`)) return;
    try {
      await adminService.partners.remove(partner.id);
      await loadData();
    } catch {
      setError('Não foi possível remover o parceiro.');
    }
  };

  return (
    <section className="min-h-full">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-accent/70">Rede local</p>
          <h1 className="mt-1 font-serif text-3xl font-black text-accent">Gestão de Parceiros</h1>
          <p className="mt-2 max-w-2xl text-sm text-fg/55">Administra parceiros comerciais ligados às cidades e às ofertas do jogo.</p>
        </div>
        <button type="button" onClick={() => { resetForm(); setModalOpen(true); }} className="rounded-lg border border-accent/40 bg-accent/10 px-4 py-2 text-sm font-bold text-accent hover:bg-accent/20">
          Novo parceiro
        </button>
      </div>

      {(error || notice) && (
        <div className={`mt-5 rounded-lg border p-4 text-sm font-bold ${error ? 'border-energy/40 bg-energy/10 text-energy' : 'border-forest-light/40 bg-forest-light/10 text-forest-light'}`}>
          {error || notice}
        </div>
      )}

      <section className="mt-6 rounded-lg border border-fg/10 bg-fg/[0.04]">
        <div className="flex items-center justify-between border-b border-fg/10 p-4">
          <h2 className="font-serif text-xl font-black text-accent">Parceiros registados</h2>
          <span className="rounded-full border border-fg/10 bg-black/20 px-3 py-1 text-xs font-bold text-fg/55">{loading ? 'A carregar...' : `${partners.length} parceiro(s)`}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[920px] text-left text-sm">
            <thead className="text-xs uppercase tracking-[0.16em] text-fg/35">
              <tr>
                <th className="px-4 py-3">Parceiro</th>
                <th className="px-4 py-3">Cidade</th>
                <th className="px-4 py-3">Categoria</th>
                <th className="px-4 py-3">Contacto</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {!loading && partners.length === 0 && <tr><td className="px-4 py-6 text-fg/45" colSpan="6">Nenhum parceiro registado.</td></tr>}
              {partners.map((partner) => (
                <tr key={partner.id}>
                  <td className="px-4 py-3"><div className="font-bold text-fg">{partner.name}</div><div className="text-xs text-fg/40">{partner.address || partner.description || 'Sem morada'}</div></td>
                  <td className="px-4 py-3 text-fg/60">{cityById.get(partner.cityId)?.name || '-'}</td>
                  <td className="px-4 py-3 capitalize text-fg/60">{partner.category}</td>
                  <td className="px-4 py-3 text-fg/60">{partner.email || partner.phone || '-'}</td>
                  <td className="px-4 py-3">
                    <button type="button" onClick={() => toggleActive(partner)} className={`rounded-full border px-3 py-1 text-xs font-bold ${partner.isActive ? 'border-forest-light/30 bg-forest-light/10 text-forest-light' : 'border-fg/15 bg-fg/5 text-fg/45'}`}>
                      {partner.isActive ? 'Ativo' : 'Inativo'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button type="button" onClick={() => editPartner(partner)} className="mr-2 rounded-lg border border-ocean-light/30 px-3 py-1.5 text-xs font-bold text-ocean-light hover:bg-ocean-light/10">Editar</button>
                    <button type="button" onClick={() => removePartner(partner)} className="rounded-lg border border-energy/30 px-3 py-1.5 text-xs font-bold text-energy hover:bg-energy/10">Remover</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} className="max-w-5xl">
        <div className="max-h-[86vh] overflow-y-auto rounded-lg border border-accent/25 bg-background p-5 shadow-gold">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-accent/60">Parceiros</p>
              <h2 className="font-serif text-2xl font-black text-accent">{editingId ? 'Editar parceiro' : 'Novo parceiro'}</h2>
            </div>
            <button type="button" onClick={() => setModalOpen(false)} className="rounded-lg border border-fg/10 px-3 py-1.5 text-sm font-bold text-fg/60 hover:bg-fg/10">Fechar</button>
          </div>
          <form className="mt-5 grid gap-3" onSubmit={savePartner}>
            <label className="grid gap-1"><span className={labelClass}>Cidade</span><select className={fieldClass} value={form.cityId} onChange={(e) => setForm({ ...form, cityId: e.target.value })} required>{cities.map((city) => <option key={city.id} value={city.id}>{city.name}</option>)}</select></label>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="grid gap-1"><span className={labelClass}>Nome</span><input className={fieldClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></label>
              <label className="grid gap-1"><span className={labelClass}>Categoria</span><input className={fieldClass} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></label>
            </div>
            <label className="grid gap-1"><span className={labelClass}>Descrição</span><textarea className={`${fieldClass} min-h-24 resize-y`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
            <label className="grid gap-1"><span className={labelClass}>Morada</span><input className={fieldClass} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></label>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="grid gap-1"><span className={labelClass}>Email</span><input className={fieldClass} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
              <label className="grid gap-1"><span className={labelClass}>Telefone</span><input className={fieldClass} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></label>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="grid gap-1"><span className={labelClass}>Latitude</span><input className={fieldClass} type="number" step="0.0000001" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} /></label>
              <label className="grid gap-1"><span className={labelClass}>Longitude</span><input className={fieldClass} type="number" step="0.0000001" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} /></label>
            </div>
            <div className="grid gap-3 rounded-lg border border-fg/10 bg-black/15 p-3 sm:grid-cols-[160px_1fr]">
              <div className="grid h-32 place-items-center overflow-hidden rounded-lg border border-fg/10 bg-black/25">
                {logoPreview ? (
                  <img src={logoPreview} alt="Pré-visualização da logo" className="h-full w-full object-contain p-3" />
                ) : (
                  <span className="px-3 text-center text-sm text-fg/35">Logo do parceiro</span>
                )}
              </div>
              <div className="grid content-center gap-2">
                <span className={labelClass}>Logo para publicidade no jogo</span>
                <input
                  className="block w-full text-sm text-fg/70 file:mr-3 file:rounded-lg file:border-0 file:bg-accent file:px-3 file:py-2 file:font-bold file:text-fg"
                  type="file"
                  accept="image/*"
                  onChange={(e) => selectLogo(e.target.files?.[0])}
                />
                <input className={fieldClass} value={form.logoUrl} onChange={(e) => { setForm({ ...form, logoUrl: e.target.value }); setLogoPreview(resolveAssetUrl(e.target.value)); }} placeholder="Ou cola uma URL externa" />
                <p className="text-xs text-fg/40">Preferível PNG/WebP com fundo transparente ou marca bem legível.</p>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-1">
              <label className="grid gap-1"><span className={labelClass}>Imagem URL</span><input className={fieldClass} value={form.imageUrl} onChange={(e) => setForm({ ...form, imageUrl: e.target.value })} /></label>
            </div>
            <label className="flex items-center gap-2 rounded-lg border border-fg/10 bg-black/15 p-3 text-sm font-bold text-fg/70"><input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />Ativo</label>
            <button type="submit" disabled={saving} className="rounded-lg bg-accent px-4 py-3 font-serif font-black text-fg shadow-gold disabled:opacity-60">{saving ? 'A guardar...' : editingId ? 'Guardar alterações' : 'Criar parceiro'}</button>
          </form>
        </div>
      </Modal>
    </section>
  );
}
